if (typeof G == 'undefined') G = {};

(function GAsyncUtilsIIFE() {

  G.AsyncUtils = {
    createTrackingScheduler:        createTrackingScheduler,
    promisifyCallbacks:             promisifyCallbacks,
    createFuturePromise:            createFuturePromise,
    waitForAllPromisesToSettle:     waitForAllPromisesToSettle,
    wrapToDoOnce:                   wrapToDoOnce,
    delay:                          delay,
    createScheduler:                createScheduler,
    retryAsyncAction:               retryAsyncAction,
    createWaiter:                   createWaiter,
    createPhaserStateStatusTracker: createPhaserStateStatusTracker,
    debounce:                       debounce
  };

  return;

  /**
   * @param addFunc ((...any) => T)
   *     must return something unique that will be later passed to `removeFunc`
   * @param removeFunc ((unique: T) => void)
   */
  function createTrackingScheduler(addFunc, removeFunc) {

    var tracked = [];

    return {
      add:       add,
      remove:    remove,
      removeAll: removeAll
    };

    function add(/*arguments*/) {
      var result = addFunc.apply(null, arguments);
      track(result);
      return result;
    }

    function remove(result) {
      if (untrack(result)) {
        removeFunc(result);
      }
    }

    function removeAll() {
      while (tracked.length > 0) {
        remove(tracked[0]);
      }
    }

    function track(result) {
      debugCheck(result);
      tracked.push(result);
    }

    function untrack(result) {
      var index = tracked.indexOf(result);
      if (index >= 0) {
        tracked.splice(index, 1);
        return true;
      }
      return false;
    }

    function debugCheck(result) {
      console.assert(
        tracked.indexOf(result) === -1,
        "addFunc must return something unique"
      );
    }
  }

  function promisifyCallbacks(
    optDoneCallback,
    optFailCallback,
    optAsyncStartCallback,
    optDoneSyncCallback
  ) {

    return {
      finishSync: finishSync,
      startAsync: startAsync
    };

    function finishSync(value) {
      if (optDoneSyncCallback) {
        optDoneSyncCallback(value);
      } else {
        optDoneCallback && optDoneCallback(value);
      }
      return Promise.resolve(value);
    }

    function startAsync(promise) {
      optAsyncStartCallback && optAsyncStartCallback();
      return promise.then(
        function onSuccess(result) {
          optDoneCallback && optDoneCallback(result);
          return result;
        },
        function onFail(error) {
          optFailCallback && optFailCallback(textureName);
          return Promise.reject(error);
        }
      )
    }
  }

  // A promise that you can resolve or reject outside of a function passed to its constructor
  function createFuturePromise(optDefaultValue) {

    var settled = false;
    var succeed = false;
    var value = optDefaultValue;

    var fulfillFunc;
    var rejectFunc;

    var promise = new Promise(function(fulfill, reject) {
      fulfillFunc = fulfill;
      rejectFunc  = reject;
    });

    return {
      read: {
        promise: promise,
        isSettled: isSettled,
        isSucceed: isSucceed,
        getValue: getValue
      },
      control: {
        fulfill: fulfill,
        reject: reject
      }
    };

    function isSettled() { return settled; }
    function isSucceed() { return succeed; }
    function getValue() { return value; }

    function fulfill(result) {
      if (!settled) {
        settled = true;
        succeed = true;
        value = result;
        fulfillFunc(value);
      }
    }

    function reject(error) {
      if (!settled) {
        settled = true;
        rejectFunc(error);
      }
    }
  }

  function waitForAllPromisesToSettle(promises) {

    return Promise.all(promises.map(wrapPromise)).then(function(results) {
      const success = results.every(function(result) { return result.success });
      return success ? Promise.resolve(results) : Promise.reject(results);
    });

    function wrapPromise(promise) {
      return promise.then(
        function(value) { return { value: value, success: true  }; },
        function(error) { return { error: error, success: false }; }
      );
    }
  }

  function wrapToDoOnce(func, optContext) {
    var funcToCallNextTime = func;
    var context = optContext;
    return wrapped;
    function wrapped() {
      if (funcToCallNextTime) {
        var temp = funcToCallNextTime;
        funcToCallNextTime = null;
        temp.apply(context, arguments);
        context = null;
      }
    }
  }

  /**
   * @param arg {false|Phaser.Game} see `G.AsyncUtils.createScheduler`
   */
  function delay(arg, func, delayMs) {
    var scheduler = createScheduler(arg);
    scheduler.rescheduleAction(func, delayMs);
    return cancel;
    function cancel() { scheduler.cancelSchedule(); }
  }

  /**
   * Helper when you need to schedule an action to be performed after a delay,
   * but you might need to re-schedule it or cancel
   *
   * @param arg {false|Phaser.Game} if false, the scheduler will use setTimeout
   */
  function createScheduler(arg, optDefaultDelayMs) {

    var cancelFunc = null;
    var schedule = getScheduleFunc(arg);

    return {
      isRunning:        isRunning,
      rescheduleAction: rescheduleAction,
      cancelSchedule:   cancelSchedule
    };

    function isRunning() {
      return cancelFunc !== null;
    }

    function rescheduleAction(action, optDelayMs) {
      cancelSchedule();
      var delayMs = G.Utils.defined(optDelayMs, optDefaultDelayMs, 1000);
      cancelFunc = schedule(delayMs, function() {
        cancelFunc = null;
        action();
      });
    }

    function cancelSchedule() {
      if (isRunning()) {
        var cancelFuncCopy = cancelFunc;
        cancelFunc = null;
        cancelFuncCopy();
      }
    }

    function getScheduleFunc(arg) {

      if (arg === false) {
        return scheduleTimeout;
      } else {
        if (!arg) throw TypeError('Argument must be an instance of Phaser.Game or a false');
        var game = arg;
        var timer = game.time.events;
        return scheduleTimeEvent;
      }

      function scheduleTimeEvent(delayMs, func) {
        var timeEvent = timer.add(delayMs, func);
        return cancel;
        function cancel() { timer.remove(timeEvent); }
      }

      function scheduleTimeout(delayMs, func) {
        var timeoutId = setTimeout(func, delayMs);
        return cancel;
        function cancel() { clearTimeout(timeoutId); }
      }
    }
  }

  /**
   * @param action {(callback: (succeed: Boolean) => void) => (cancel: () => void)}
   * @param [optGame] {Phaser.Game} if provided, will use game's timer
   * @param [optGetDelayFunc] {(count: Number) => Number}
   */
  function retryAsyncAction(action, optGame, optGetDelayFunc) {

    var scheduler = createScheduler(optGame || false);
    var increasingDelayManager = createIncreasingDelayManager(optGetDelayFunc);

    var running = false;
    function isRunning() { return running; }

    var cancelled = false;
    function isCancelled() { return cancelled; }

    var cancelAction = null;

    scheduleRetry();

    return {
      isRunning: isRunning,
      isCancelled: isCancelled,
      cancel: cancel,
      resetDelays: resetDelays
    };

    function scheduleRetry() {
      if (isRunning()) return;
      scheduler.cancelSchedule();
      var delayMs = Math.max(0, increasingDelayManager.getDelay());
      if (isFinite(delayMs)) scheduler.rescheduleAction(retry, delayMs);
    }

    function retry() {

      if (isCancelled()) throw 'Retry after cancel is not supported';

      if (isRunning()) return;

      running = true;

      var cancelActionWasNulled = false;
      cancelAction = action(
        function callback(succeed) {
          running = false;

          cancelAction = null;
          cancelActionWasNulled = true;

          if (!isCancelled()) {
            succeed ? handleSuccess() : handleFail();
          }
        }
      );
      if (cancelActionWasNulled) cancelAction = null;
    }

    function handleSuccess() {
      // Nothing to do
    }

    function handleFail() {
      increasingDelayManager.increase();
      scheduleRetry();
    }

    function cancel() {
      if (isCancelled()) return;
      cancelled = true;
      running = false;
      scheduler.cancelSchedule();
      cancelAction && cancelAction();
    }

    function resetDelays() {
      if (isCancelled()) throw 'Reset after cancel is not supported';
      increasingDelayManager.reset();
      scheduleRetry();
    }

    //

    function createIncreasingDelayManager(optGetDelayFunc) {
      var counter = 0;
      var getDelayFunc = optGetDelayFunc || createDefaultGetDelayFunc(1000, 30 * 1000);

      return {
        getDelay: getDelay,
        increase: increase,
        reset: reset
      };

      function getDelay() { return (counter === 0) ? 0 : getDelayFunc(counter); }
      function increase() { counter += 1; }
      function reset()    { counter = 0;  }

      //

      function createDefaultGetDelayFunc(initialDelay, maxDelay) {
        return getDelayFunc;
        function getDelayFunc(counter) {
          return Math.min(maxDelay, initialDelay * Math.pow(2, counter - 1));
        }
      }
    }
  }

  /**
   * @param arg {false|Phaser.Game} see `G.AsyncUtils.createScheduler`
   */
  function createWaiter(
    arg,
    optStoppedBeforeTimeoutFunc,
    optTimedOutFunc,
    optStoppedAfterTimeoutFunc
  ) {

    var timer = createScheduler(arg);
    var timedOut = false;

    var signals = {
      stoppedBeforeTimeout: new Phaser.Signal(),
      timedOut:             new Phaser.Signal(),
      stoppedAfterTimeout:  new Phaser.Signal()
    };

    return {
      signals: signals,
      startWaiting: startWaiting,
      stopWaiting: stopWaiting
    };

    function startWaiting(durationMs) {
      timer.rescheduleAction(
        function() {
          timedOut = true;
          signals.timedOut.dispatch();
        },
        durationMs
      );
    }

    function stopWaiting(succeed) {
      timer.cancelSchedule();
      
      if (!timedOut) {
        signals.stoppedBeforeTimeout.dispatch(succeed);
      } else {
        signals.stoppedAfterTimeout.dispatch(succeed);
      }
    }

  }

  function createPhaserStateStatusTracker(game) {
    return createAliveStatusTracker(
      function(callback) { game.state.onStateChange.addOnce(callback); }
    );
  }

  function createAliveStatusTracker(registerKillerListener) {

    var killedSignal = new Phaser.Signal();
    var deadReasons = new G.Reasons(function(dead) { if (dead) killedSignal.dispatch(); });

    registerKillerListener(kill);

    return {
      isAlive: deadReasons.hasNone,
      killedSignal: killedSignal
    };

    function kill() { deadReasons.add('killed'); }
  }

  function debounce(getter, durationMs) {
    var last = -Infinity;
    var saved;
    return debounced;
    function debounced(/*arguments*/) {
      var now = Date.now();
      if (now - last >= durationMs) {
        last = now;
        saved = getter.apply(this, arguments);
      }
      return saved;
    }
  }

}());
